/**
 * PreviewCard — renders the server's column-confirm preview: barcode count,
 * duplicates, invalid rows, first-5 sample. Brian clicks "looks right"
 * to move into event-pick.
 *
 */

import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import type { ColumnConfirmResult } from '../api'
import { AgentBubble } from './AgentBubble'

interface PreviewCardProps {
  confirm: ColumnConfirmResult
  columnHeader: string
  busy: boolean
  onAccept: () => void
  onBack: () => void
}

export function PreviewCard({
  confirm,
  columnHeader,
  busy,
  onAccept,
  onBack,
}: PreviewCardProps) {
  const hasInvalid = confirm.invalid.length > 0
  const firstInvalid = confirm.invalid[0]
  return (
    <div className="w-full max-w-xl space-y-4">
      <AgentBubble from="agent">
        Column <strong>"{columnHeader}"</strong> —{' '}
        <strong>{confirm.barcodeCount.toLocaleString()}</strong> barcodes,{' '}
        {confirm.duplicates.toLocaleString()} duplicate
        {confirm.duplicates === 1 ? '' : 's'}.
      </AgentBubble>

      {hasInvalid && firstInvalid ? (
        <AgentBubble from="agent">
          <span className="inline-flex items-start gap-1.5">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <span>
              {confirm.invalid.length} row
              {confirm.invalid.length === 1 ? '' : 's'} don't look like
              barcodes — <code className="rounded bg-slate-200 px-1 dark:bg-slate-700">{firstInvalid.value}</code>{' '}
              {firstInvalid.reason === 'scientific-notation'
                ? 'has an E+ in it, which usually means Excel squashed the number.'
                : firstInvalid.reason}
              . I'll skip those unless you want to bail and re-export as text.
            </span>
          </span>
        </AgentBubble>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Preview
        </div>
        <ul className="space-y-1 font-mono text-xs text-slate-800 dark:text-slate-200">
          {confirm.preview.map((val, i) => (
            <li key={`${val}-${i}`} className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3 text-emerald-500" />
              {val}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy || confirm.barcodeCount === 0}
          onClick={onAccept}
          className="inline-flex items-center gap-1.5 rounded-lg bg-carnival-400 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-carnival-500 disabled:opacity-50"
        >
          Looks right
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onBack}
          className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          Wrong column, go back
        </button>
      </div>
    </div>
  )
}
